import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import api, { handleApiError } from "../../helpers/api";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const BestSellingProducts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [period, setPeriod] = useState("Today");
    const [page, setPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const limit = 5;

    const fetchProducts = async (selectedPeriod, currentPage) => {
        try {
            let dateFilter = {};
            const now = new Date();
            switch (selectedPeriod) {
                case "Today":
                    dateFilter = {
                        createdAt: { $gte: new Date(now.setHours(0, 0, 0, 0)) },
                    };
                    break;
                case "Yesterday":
                    dateFilter = {
                        createdAt: {
                            $gte: new Date(now.setHours(0, 0, 0, 0) - 24 * 60 * 60 * 1000),
                            $lt: new Date(now.setHours(0, 0, 0, 0)),
                        },
                    };
                    break;
                case "Last 7 Days":
                    dateFilter = {
                        createdAt: { $gte: new Date(now.setDate(now.getDate() - 7)) },
                    };
                    break;
                case "Last 30 Days":
                    dateFilter = {
                        createdAt: { $gte: new Date(now.setDate(now.getDate() - 30)) },
                    };
                    break;
                case "This{keyword:This Month":
                    dateFilter = {
                        createdAt: { $gte: new Date(now.getFullYear(), now.getMonth(), 1) },
                    };
                    break;
                case "Last Month":
                    dateFilter = {
                        createdAt: {
                            $gte: new Date(now.getFullYear(), now.getMonth() - 1, 1),
                            $lt: new Date(now.getFullYear(), now.getMonth(), 1),
                        },
                    };
                    break;
                default:
                    dateFilter = {};
            }

            const res = await api.get("/products", {
                params: {
                    sort: "-sold",
                    limit,
                    skip: (currentPage - 1) * limit,
                    ...(selectedPeriod !== "All" && { createdAt: JSON.stringify(dateFilter.createdAt) }),
                },
            });

            const allProducts = res.data.data || [];
            // Client-side pagination fallback if backend doesn't support skip/limit
            const start = (currentPage - 1) * limit;
            const paginatedProducts = allProducts.slice(start, start + limit);
            setProducts(paginatedProducts);
            setTotalProducts(res.data.total || allProducts.length);
        } catch (err) {
            handleApiError(err, dispatch, navigate);
            setProducts([]);
            setTotalProducts(0);
        }
    };

    useEffect(() => {
        fetchProducts(period, page);
    }, [period, page, dispatch, navigate]);

    const handlePeriodChange = (newPeriod) => {
        setPeriod(newPeriod);
        setPage(1); // Reset to first page on period change
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const totalPages = Math.ceil(totalProducts / limit);

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardHeader className="align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">Best Selling Products</h4>
                        <div className="flex-shrink-0">
                            <UncontrolledDropdown className="card-header-dropdown" direction="start">
                                <DropdownToggle tag="a" className="text-reset" role="button">
                                    <span className="fw-semibold text-uppercase fs-12">Sort by: </span>
                                    <span className="text-muted">{period}<i className="mdi mdi-chevron-down ms-1"></i></span>
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                    <DropdownItem onClick={() => handlePeriodChange("Today")}>Today</DropdownItem>
                                    <DropdownItem onClick={() => handlePeriodChange("Yesterday")}>Yesterday</DropdownItem>
                                    <DropdownItem onClick={() => handlePeriodChange("Last 7 Days")}>Last 7 Days</DropdownItem>
                                    <DropdownItem onClick={() => handlePeriodChange("Last 30 Days")}>Last 30 Days</DropdownItem>
                                    <DropdownItem onClick={() => handlePeriodChange("This Month")}>This Month</DropdownItem>
                                    <DropdownItem onClick={() => handlePeriodChange("Last Month")}>Last Month</DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </div>
                    </CardHeader>

                    <CardBody>
                        <div className="table-responsive table-card">
                            <table className="table table-hover table-centered align-middle table-nowrap mb-0">
                                <tbody>
                                {(products || []).map((item, key) => (
                                    <tr key={key}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-sm bg-light rounded p-1 me-2">
                                                    <img src={item.images[0].url} alt="" className="img-fluid d-block" />
                                                </div>
                                                <div>
                                                    <h5 className="fs-14 my-1">
<Link to={`/apps-ecommerce-product-details/${item._id}`} className="text-reset">{item.name}</Link>                                                    </h5>
                                                    <span className="text-muted">{new Date(item.createdAt).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <h5 className="fs-14 my-1 fw-normal">DT {item.price.toFixed(2)}</h5>
                                            <span className="text-muted">Price</span>
                                        </td>
                                        <td>
                                            <h5 className="fs-14 my-1 fw-normal">{item.sold || 0}</h5>
                                            <span className="text-muted">Orders</span>
                                        </td>
                                        <td>
                                            <h5 className="fs-14 my-1 fw-normal">
                                                {item.stock > 0 ? item.stock : <span className="badge badge-soft-danger">Out of stock</span>}
                                            </h5>
                                            <span className="text-muted">Stock</span>
                                        </td>
                                        <td>
                                            <h5 className="fs-14 my-1 fw-normal">DT {(item.price * (item.sold || 0)).toFixed(2)}</h5>
                                            <span className="text-muted">Amount</span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="align-items-center mt-4 pt-2 justify-content-between row text-center text-sm-start">
                            <div className="col-sm">
                                <div className="text-muted">
                                    Showing <span className="fw-semibold">{products.length}</span> of <span className="fw-semibold">{totalProducts}</span> Results
                                </div>
                            </div>
                            <div className="col-sm-auto mt-3 mt-sm-0">
                                <ul className="pagination pagination-separated pagination-sm mb-0">
                                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                                        <Link to="#" className="page-link" onClick={() => handlePageChange(page - 1)}>←</Link>
                                    </li>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <li key={i} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                                            <Link to="#" className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</Link>
                                        </li>
                                    ))}
                                    <li className={`page-item ${page === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                                        <Link to="#" className="page-link" onClick={() => handlePageChange(page + 1)}>→</Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </React.Fragment>
    );
};

export default BestSellingProducts;